import { useRef, useState } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";

const SUGGESTIONS = [
  "How many paid users do we have and what's the conversion rate?",
  "What's our lifetime revenue and how does it break down by tier?",
  "Which event types are most popular right now?",
  "Draft a segment idea for a Pro upsell email to free users",
];

export default function AdminAI() {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const ref = useRef(null);

  const send = async (t) => {
    const text = (t ?? draft).trim();
    if (!text || sending) return;
    setDraft("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next); setSending(true);
    try {
      const r = await api.post("/admin/ai/chat", { message: text, history: next.slice(-9, -1) });
      setMessages([...next, { role: "assistant", content: r.data?.reply || "—" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "AI briefly unavailable";
      setMessages([...next, { role: "assistant", content: msg, error: true }]);
    } finally { setSending(false); setTimeout(() => ref.current?.scrollTo({ top: 1e9 }), 50); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl" data-testid="admin-ai">
      <div className="flex items-baseline gap-3">
        <h1 className="font-display text-2xl font-semibold">AI helper</h1>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] font-semibold">Read-only</span>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">Ask about the platform. The AI reads live KPIs and never mutates data — every action is confirmed by you in the panel.</p>

      <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden flex flex-col" style={{ height: "64vh" }}>
        <div ref={ref} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
          {messages.length === 0 && (
            <>
              <div className="rounded-2xl rounded-tl-md bg-[hsl(var(--muted))] px-3.5 py-2.5 leading-relaxed">Hi. Ask me anything about GatherVoices activity, revenue, or which users to reach next.</div>
              <div className="flex flex-wrap gap-2">{SUGGESTIONS.map((q, i) => <button key={i} data-testid={`admin-ai-suggestion-${i}`} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] hover:bg-[hsl(var(--muted))]">{q}</button>)}</div>
            </>
          )}
          {messages.map((m, i) => m.role === "user" ? (
            <div key={i} className="flex justify-end"><div className="max-w-[80%] rounded-2xl rounded-tr-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-3.5 py-2.5 whitespace-pre-wrap">{m.content}</div></div>
          ) : (
            <div key={i} data-testid="admin-ai-assistant-msg" className={`max-w-[92%] rounded-2xl rounded-tl-md px-3.5 py-2.5 whitespace-pre-wrap leading-relaxed ${m.error ? "bg-[hsl(var(--destructive))]/10" : "bg-[hsl(var(--muted))]"}`}>{m.content}</div>
          ))}
          {sending && <div className="rounded-2xl rounded-tl-md bg-[hsl(var(--muted))] px-3.5 py-2.5 inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))]"><Loader2 size={14} className="animate-spin" /> Thinking…</div>}
        </div>
        <div className="border-t border-[hsl(var(--border))] p-3 flex items-end gap-2">
          <textarea data-testid="admin-ai-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Ask about users, revenue, events…" className="flex-1 max-h-32 min-h-[38px] rounded-xl px-3 py-2 text-sm bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none" />
          <button data-testid="admin-ai-send" onClick={() => send()} disabled={sending || !draft.trim()} className="h-10 w-10 grid place-items-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] disabled:opacity-40" aria-label="Send"><Send size={14} /></button>
        </div>
        <div className="px-3 pb-2 flex items-center gap-1 text-[10.5px] text-[hsl(var(--muted-foreground))]"><Sparkles size={11} className="text-[hsl(var(--accent))]" /> Every suggestion the AI makes must be confirmed by you in the admin panel.</div>
      </div>
    </div>
  );
}
